import { atom } from "jotai";
import * as yup from "yup";

import { FormQuestionConfiguration } from "@whoosh/design-system/src/components/controls/EventFormRenderer";
import { decodeCheckboxSelection } from "@whoosh/design-system/src/components/controls/EventFormRenderer/checkboxSelection";

import { EventFormResponseInput } from "../hooks/__generated__/useUpdateEventSignUpMutation.graphql";
import { EventSignUpRegistration, EventSignUpRow } from "../types";
import { eventSignUpsReducerAtom } from "./groupEvent";

export const isFormModalOpenAtom = atom<boolean>(false);

export const EventDetailFormConfigurationAtom = atom<
  FormQuestionConfiguration[] | null
>(null);

export const EventDetailEventHasFormAtom = atom((get) => {
  const formConfiguration = get(EventDetailFormConfigurationAtom);

  return !!formConfiguration?.length;
});

type eventFormResponsesReducerActions =
  | { type: "clear" }
  | { type: "reset" }
  | { type: "initialize"; skipModal?: boolean }
  | {
      type: "updateFormQuestionResponse";
      payload: {
        id: string;
        value?: string;
        booleanValue?: boolean;
        transportationTypeId?: string;
        registration: EventSignUpRegistration;
      };
    };

export const eventFormResponsesSignUpRowAtom = atom<null | EventSignUpRow>(
  null
);

export const isCurrentEventFormValidAtom = atom((get) => {
  const questionConfiguration = get(EventDetailFormConfigurationAtom);
  const currentSignUpRow = get(eventFormResponsesSignUpRowAtom);

  /**
   * We always need to validate email
   */
  const questionsToValidate = questionConfiguration?.filter(
    (q) =>
      !!q.enabled &&
      (q.type === "EMAIL" ||
        q.guestVisibility === "REQUIRED" ||
        q.memberVisibility === "REQUIRED")
  );

  const isValid = !!questionsToValidate?.every((q) => {
    return currentSignUpRow?.registrations.every((r) => {
      const isMember = r.type === "MEMBER";
      const isRequired = isMember
        ? q.memberVisibility === "REQUIRED"
        : q.guestVisibility === "REQUIRED";
      /**
       * Currently only Email field types have validation outside of 'required'
       */
      if (!isRequired && q.type !== "EMAIL") return true;

      const formResponse = r.formResponses?.find(
        (f) => f.questionConfigurationId === q.id
      );

      const { stringValue, booleanValue, transportationTypeId } =
        formResponse ?? {};

      switch (q.type) {
        case "BOOLEAN":
          return booleanValue !== undefined;
        case "TOGGLE":
          return booleanValue !== undefined;
        case "TEXT":
          if (!stringValue) {
          }
          return !!stringValue;
        case "LONG_TEXT":
          return !!stringValue;
        case "EMAIL":
          //If it isn't required, and there is no value, don't validate
          if (!isRequired && !stringValue) return true;
          const schema = yup.string().email();
          let isEmail = false;
          try {
            isEmail = !!schema.validateSync(stringValue);
          } catch {
            isEmail = false;
          }
          return !!stringValue && isEmail;
        case "GHIN_NUMBER":
          return !!stringValue;
        case "TRANSPORTATION":
          if (!!transportationTypeId) {
          }
          return !!transportationTypeId;
        case "MULTIPLE_CHOICE":
          return !!stringValue;
        case "CHECKBOXES":
          return !!decodeCheckboxSelection(stringValue, q.values)?.length;
        default:
          return !!stringValue;
      }
    });
  });

  return isValid;
});

export const eventFormResponsesActionsAtom = atom(
  null,
  (get, set, action: eventFormResponsesReducerActions) => {
    const currentSignUp = get(eventFormResponsesSignUpRowAtom);
    const eventFormQuestionConfigurations = get(
      EventDetailFormConfigurationAtom
    );

    switch (action.type) {
      case "reset":
        set(eventFormResponsesSignUpRowAtom, null);
        set(isFormModalOpenAtom, true);
        break;
      case "clear":
        set(eventFormResponsesSignUpRowAtom, null);
        set(isFormModalOpenAtom, false);
        break;
      case "initialize":
        if (!action.skipModal) {
          set(isFormModalOpenAtom, true);
        }
        const currentEventSigupRow = get(eventSignUpsReducerAtom);
        set(eventFormResponsesSignUpRowAtom, {
          ...currentEventSigupRow,
        });
        break;
      case "updateFormQuestionResponse":
        const { value, booleanValue, transportationTypeId, id, registration } =
          action.payload;

        if (currentSignUp) {
          const newSignUpRow = { ...currentSignUp };

          const curRegistration = newSignUpRow?.registrations?.find(
            (r) => r.index === registration.index
          );

          const formState =
            eventFormQuestionConfigurations?.map((f) => {
              const formResponse = curRegistration?.formResponses?.find(
                (fr) => fr.questionConfigurationId === f.id
              );

              return {
                id: f.id ?? "",
                value: formResponse?.stringValue ?? undefined,
                booleanValue: formResponse?.booleanValue ?? undefined,
                transportationTypeId:
                  formResponse?.transportationTypeId ?? undefined,
              };
            }) || [];
          const formResponses: EventFormResponseInput[] = formState.map((f) => {
            if (f.id === id) {
              return {
                stringValue: value,
                booleanValue,
                questionConfigurationId: id,
                transportationTypeId,
              };
            } else {
              return {
                stringValue: f.value,
                booleanValue: f.booleanValue,
                questionConfigurationId: f.id,
                transportationTypeId: f.transportationTypeId,
              };
            }
          });

          newSignUpRow.registrations = newSignUpRow.registrations?.map(
            (r, i) => {
              if (i === registration.index) {
                return {
                  ...r,
                  formResponses,
                };
              } else {
                return r;
              }
            }
          );

          set(eventFormResponsesSignUpRowAtom, newSignUpRow);
        }
        break;
      default:
        break;
    }
  }
);
